import React, { Suspense } from "react";
import { ChakraProvider, Spinner, Center } from "@chakra-ui/react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { apolloClient } from "./apollo/client";
import Home from "./pages/Home";
// Podcast import removed
import Doctorate from "./pages/Doctorate";
import BdayPage from "./pages/bday";
import BdayRsvpPage from "./pages/bday/rsvp";
import OneGroupProposal from "./pages/offers/onegroup";
import WattDropProposal from "./pages/offers/wattdrop";
import PaulNiedererProposal from "./pages/offers/paulniederer";
// import OneGroupStructuredProposal from "./pages/offers/onegroupstructured";
import { brandConfig } from "./brandConfig";
import theme from "./theme";

// Module configurations
import clientsModuleConfig from "./pages/clients/moduleConfig";
import passwordsModuleConfig from "./pages/passwords/moduleConfig";
import employeesModuleConfig from "./pages/employees/moduleConfig";
import companiesModuleConfig from "./pages/companies/moduleConfig";
import seoMetadataModuleConfig from "./pages/seometadata/moduleConfig";
import CompaniesList from "./pages/companies/CompaniesList";
import NewCompany from "./pages/companies/NewCompany";
import CompanyDetails from "./pages/companies/CompanyDetails";
import EditCompany from "./pages/companies/EditCompany";
import assetsModuleConfig from "./pages/assets/moduleConfig";
import { calendarsModuleConfig } from "./pages/calendars/moduleConfig";
import opportunitiesModuleConfig from "./pages/opportunities/moduleConfig";
import youtubeToIPFSModuleConfig from "./pages/youtubetoipfs/moduleConfig";
// videoServerModuleConfig removed - module deleted
import meetingSummarizerModuleConfig from "./pages/meeting-summarizer/moduleConfig";
import emailsModuleConfig from "./pages/emails/moduleConfig";
import transcriptionsModuleConfig from "./pages/transcriptions/moduleConfig";
import proposalsModuleConfig from "./pages/proposals/moduleConfig";
import companyKnowledgeModuleConfig from "./pages/companyknowledge/moduleConfig";
import lmModuleConfig from "./pages/lm/moduleConfig";
import frontendUpgradesModuleConfig from "./pages/frontend-upgrades/moduleConfig";
import modulesModuleConfig from "./pages/modules/moduleConfig";
import siteEditorModuleConfig from "./pages/site-editor/moduleConfig";
import masterSiteEditorModuleConfig from "./pages/master-site-editor/moduleConfig";
import analyticsModuleConfig from "./pages/analytics/moduleConfig";
import mediaCDNModuleConfig from "./pages/media-cdn/moduleConfig";
// autoDjiUploadModuleConfig removed - module not yet created
import bdayModuleConfig from "./pages/bday/moduleConfig";
import zkAuthenticationModuleConfig from "./pages/zk-authentication/moduleConfig";
import mechanicJobsModuleConfig from "./pages/mechanic-jobs/moduleConfig";
import pModuleConfig from "./pages/p/moduleConfig";
import rewardsModuleConfig from "./pages/rewards/moduleConfig";
import investorsModuleConfig from "./pages/investors/moduleConfig";
import investmentProjectsModuleConfig from "./pages/investment-projects/moduleConfig";
// videoServerModuleConfig removed - module deleted

/*
 * MIGRATION NOTE: We are transitioning to a dynamic module routing system.
 * 
 * The Clients module has been converted as the example pattern.
 * As we update each module, we will:
 * 1. Create/update its moduleConfig.ts file (see clients/moduleConfig.ts as example)
 * 2. Import the moduleConfig here
 * 3. Add it to the modules array below
 * 4. Remove its hardcoded routes from below
 * 
 * This allows modules to be truly plug-and-play with automatic route registration
 * and centralized permission management.
 * 
 * TO CONVERT: Projects, Bills, Products, Sessions, Emails, etc.
 */

// Import other module configs here as you convert them:
import billsModuleConfig from "./pages/bills/moduleConfig";
import tenantManagementModuleConfig from "./pages/tenant-management/moduleConfig";
import adminModuleConfig from "./pages/admin/moduleConfig";
// import productsModuleConfig from "./pages/products/moduleConfig";

// Combine all module configurations
const modules = [
  modulesModuleConfig, // Public modules directory - always first for easy discovery
  clientsModuleConfig,
  passwordsModuleConfig,
  employeesModuleConfig,
  companiesModuleConfig,
  seoMetadataModuleConfig, // SEO metadata module with ADMIN/ADMIN_DASHBOARD permissions
  assetsModuleConfig,
  calendarsModuleConfig,
  opportunitiesModuleConfig,
  proposalsModuleConfig, // Proposals module with PROPOSALS_VIEW/CREATE/ADMIN permissions
  youtubeToIPFSModuleConfig,
  // videoServerModuleConfig removed - module deleted
  billsModuleConfig,  // Now using dynamic routing!
  meetingSummarizerModuleConfig, // Meeting summarizer with proper permissions
  transcriptionsModuleConfig, // Transcriptions module with TRANSCRIPTION_USER/ADMIN permissions
  emailsModuleConfig, // Emails module with EMAIL_ADMIN and EMAIL_USER permissions
  companyKnowledgeModuleConfig, // Company knowledge base with COMPANY_KNOWLEDGE_* permissions
  lmModuleConfig, // Learning Management System with course creation and enrollment
  frontendUpgradesModuleConfig, // Frontend upgrade tracking with WEBSITE_MASTER permission
  tenantManagementModuleConfig, // Tenant management with ADMIN/TENANT_MASTER_ADMIN permissions
  adminModuleConfig, // System administration with ADMIN/TENANT_MASTER_ADMIN permissions
  siteEditorModuleConfig, // Site Editor - Tenant self-service website editing with Claude
  masterSiteEditorModuleConfig, // Master Site Editor - Manage all tenant websites (SITE_EDITOR_MASTER_ADMIN)
  analyticsModuleConfig, // Analytics - Privacy-first website analytics
  mediaCDNModuleConfig, // Media CDN - File hosting with IPFS storage
  // autoDjiUploadModuleConfig removed - module not yet created
  bdayModuleConfig, // Birthday RSVP - Guest management for Tom's 40th
  zkAuthenticationModuleConfig, // ZK Biometric Auth - Face-based passwordless login
  mechanicJobsModuleConfig, // Mechanic Jobs - Mobile mechanic job tracking and parts ordering
  pModuleConfig, // Proposals v2 - Enhanced proposal management with opportunity linking
  rewardsModuleConfig, // BB Token rewards - issue, track, and redeem tokens
  investorsModuleConfig, // Visa applicant and investor pipeline tracker
  investmentProjectsModuleConfig, // Investment project marketplace with EOI system
  // Add other modules here as you convert them:
  // productsModuleConfig,
];

// Protected Route Component
import { ProtectedRoute } from "./components/ProtectedRoute";

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <Center h="100vh">
    <Spinner size="xl" color={brandConfig.colors.primary} />
  </Center>
);

// Website-related imports
import NewWebsiteForm from "./pages/tenantwebsites/NewWebsiteForm";
import WebsitesList from "./pages/tenantwebsites/WebsitesList";
import WebsiteDetails from "./pages/tenantwebsites/WebsiteDetails";

// Bill-related imports (moved to dynamic routing via billsModuleConfig)
// Special bill pages that aren't in the module
import BillPaymentSuccess from "./pages/bills/BillPaymentSuccess";
import BillChaserPage from "./pages/admin/billChaserPage";



// Product-related imports
import NewProductForm from "./pages/products/NewProductForm";
import ProductView from "./pages/products/ProductView";
import AllProducts from "./pages/products/AllProducts";
import Cart from "./pages/products/Cart";
import OrderDetails from "./pages/products/OrderDetails";

// Project-related imports - Now handled by projectsModuleConfig

// Email-related imports - Now handled by emailsModuleConfig

// Calendar-related imports
import CalendarAccountsAdmin from "./pages/calendars/CalendarAccountsAdmin";

// Knowledge Base imports
// KnowledgeBase imports removed - module deleted

// Subscription/Billing-related imports
import SubscriptionsDashboard from "./pages/subscriptions/SubscriptionsDashboard";
import ManageSubscriptions from "./pages/subscriptions/ManageSubscriptions";
import PaymentMethods from "./pages/subscriptions/PaymentMethods";
import InvoiceHistory from "./pages/subscriptions/InvoiceHistory";
import BillingSettings from "./pages/subscriptions/BillingSettings";
import CreateSubscription from "./pages/subscriptions/CreateSubscription";
import ManualCharging from "./pages/subscriptions/ManualCharging";
import EditSubscription from "./pages/subscriptions/EditSubscription";

// Admin and dashboard imports
import AdminDashboard from "./pages/AdminDashboard";
import ModuleDeployment from "./pages/tenant-management/ModuleDeployment";
import WhatsNew from "./pages/admin/WhatsNew";

// Phone System imports
import { PhoneSystemDashboard } from "./pages/phone-system/PhoneSystemDashboard";
import { PhoneNumbersList } from "./pages/phone-system/PhoneNumbersList";
import { CallRecordings } from "./pages/phone-system/CallRecordings";
import { BrowserCall } from "./pages/phone-system/BrowserCall";
import { PhoneNumberAssignmentAdmin } from "./pages/phone-system/PhoneNumberAssignmentAdmin";

// Vapi Voice AI imports
import { VapiDashboard } from "./pages/vapi/VapiDashboard";
import { WebCall } from "./pages/vapi/WebCall";
import { CallLogs } from "./pages/vapi/CallLogs";
import { CallDetails } from "./pages/vapi/CallDetails";
import { Workflows } from "./pages/vapi/Workflows";
import { VapiTodos } from "./pages/vapi/VapiTodos";

// Test and development imports
import SendTestEmail from "./pages/test/SendTestEmail";
import TestUploadFile from "./pages/test/TestUploadFile";
import TestUploadEncryptedFile from "./pages/test/TestUploadEncryptedFile";
import TestAuth from "./pages/test/TestAuth";
import TestClientCart from "./pages/test/TestClientCart";
import TestUploadFileToPinata from "./pages/test/TestUploadFileToPinata";
import TestUploadAudioToPinataAndTranscribeWithn8n from "./pages/test/TestUploadAudioToPinataAndTranscribeWithn8n";



import Logos from "./pages/Logos";
import Event1Page from "./pages/events/tom/1";
import Event2Page from "./pages/events/tom/2";
import Event3Page from "./pages/events/tom/3";
import Event4Page from "./pages/events/tom/4";
import Block52EventPage from "./pages/events/tom/Block52";
import ChrisEventPage from "./pages/events/tom/chris";

// Contexts and Layout
import { AuthProvider } from "./contexts/AuthContext";
import { ModulePermissionsProvider, useModulePermissions } from "./contexts/ModulePermissionsContext";
import { IpfsGatewayLoader } from "./components/IpfsGatewayLoader";
import CreateTenant from "./pages/tenant-management/CreateTenant";
import TenantsList from "./pages/tenant-management/TenantsList";
import EditTenant from "./pages/tenant-management/EditTenant";

// Profile-related imports
import ViewProfile from "./pages/profile/ViewProfile";
import EditProfile from "./pages/profile/EditProfile";
import Settings from "./pages/profile/Settings";
import ShippingAddresses from "./pages/profile/ShippingAddresses";
import ProfilePaymentMethods from "./pages/profile/PaymentMethods";
import SubscriptionOffers from "./pages/profile/SubscriptionOffers";
import CurrentSubscriptions from "./pages/profile/CurrentSubscriptions";

// Seller Profile imports
import NewSellerProfile from "./pages/sellerprofile/NewSellerProfile";
import SellerProfilesList from "./pages/sellerprofile/SellerProfilesList";
import SellerProfileView from "./pages/sellerprofile/SellerProfileView";

// Provider Profile imports
import ProviderView from "./pages/provider/ProviderView";
import EditProvider from "./pages/provider/EditProvider";
import DiscoverProviders from "./pages/provider/DiscoverProviders";

// Research & Development imports
// ResearchAndDesign imports removed - module deleted
// import ShippingAddresses from "./pages/profile/ShippingAddresses";

// Permissions Matrix import
import Permissions from "./pages/Permissions";
// import PaymentMethods from "./pages/profile/PaymentMethods";

// Meeting Summarizer imports - Now handled by meetingSummarizerModuleConfig

// Domains module imports
import { DomainsPage } from "./pages/domains/index";
import { DomainDetails } from "./pages/domains/DomainDetails";

// Offers module imports
import { MaintenanceOffer } from "./pages/offers/onegroup/maintenance";
import SiteMaintenance from "./pages/offers/onegroup/sitemaintenance";

// Public Booking imports (NO AUTH REQUIRED)
import PublicBookingPage from "./pages/calendars/booking/PublicBookingPage";
import PublicBookingEventPage from "./pages/calendars/booking/PublicBookingEventPage";
import PublicBookingConfirmPage from "./pages/calendars/booking/PublicBookingConfirmPage";
import PublicBookingSuccessPage from "./pages/calendars/booking/PublicBookingSuccessPage";
import ManageBooking from "./pages/calendars/ManageBooking";

// Business Builder imports (NO AUTH REQUIRED - Public Pages)
import BusinessBuilder from "./pages/businessbuilder/index";
import Tokenomics from "./pages/businessbuilder/Tokenomics";
import Governance from "./pages/businessbuilder/Governance";
import Treasury from "./pages/businessbuilder/Treasury";
import Marketplace from "./pages/businessbuilder/Marketplace";
import TithingCalculator from "./pages/businessbuilder/TithingCalculator";
import Rewards from "./pages/businessbuilder/Rewards";
import Journey from "./pages/businessbuilder/Journey";
// Leaderboard is now part of rewards module (public route at /rewards/leaderboard)

// Live Event imports (NO AUTH REQUIRED - Public Pages)
import LiveEventInterest from "./pages/live-event/InterestPage";
import LiveEventRegister from "./pages/live-event/RegisterPage";
import LiveEventLive from "./pages/live-event/LivePage";
import LiveEventPurchase from "./pages/live-event/PurchasePage";
import LiveEventAdmin from "./pages/live-event/AdminPage";
import LiveEventRegistrations from "./pages/live-event/RegistrationsPage";

// Floating navbar component
import { FloatingNavbar } from "./components/FloatingNavbar";
import { useAuth } from "./contexts/AuthContext";

// Analytics tracker - tracks page views and engagement
import { AnalyticsTracker } from "./pages/analytics/components/AnalyticsTracker";


const AppContent = () => {
  const { isAuthenticated } = useAuth();
  const { permissions: backendPerms } = useModulePermissions();

  // Resolve permissionLevel → actual permission strings from backend
  // Modules using the new pattern have permissionLevel on routes/nav and empty permission arrays.
  // Modules not yet migrated still have hardcoded permission arrays — those pass through unchanged.
  const resolvedModules = React.useMemo(() => {
    return modules.map(module => {
      const modulePerms = backendPerms[module.id];
      if (!modulePerms) return module; // Not migrated yet — use hardcoded permissions

      return {
        ...module,
        routes: module.routes?.map((route: any) => {
          const level = (route as any).permissionLevel;
          if (!level) return route; // No permissionLevel — use existing permissions array
          return { ...route, permissions: (modulePerms as any)[level] || [] };
        }),
        navigation: module.navigation?.map((navItem: any) => {
          const level = (navItem as any).permissionLevel;
          if (!level) return navItem; // No permissionLevel — use existing permissions array
          return { ...navItem, permissions: (modulePerms as any)[level] || [] };
        }),
        permissions: {
          ...module.permissions,
          view: modulePerms.view || module.permissions.view,
          create: modulePerms.create || module.permissions.create,
          edit: modulePerms.edit || module.permissions.edit,
          delete: modulePerms.delete || module.permissions.delete,
          ...(modulePerms.financial ? { financial: modulePerms.financial } : {}),
        },
      };
    });
  }, [backendPerms]);

  return (
    <>
      {/* Analytics tracking - runs on every page view */}
      <AnalyticsTracker enabled={true} />

      {/* Show floating navbar only when authenticated */}
      {isAuthenticated && <FloatingNavbar modules={resolvedModules} />}

      {/* Each page now includes its own NavbarWithCallToAction and FooterWithFourColumns for consistency */}
      <Routes>
        {/* Home page */}
        <Route path="/" element={<Home />} />

        {/* Logo concepts page */}
        <Route path="/logos" element={<Logos />} />


        {/* Doctorate announcement page */}
        <Route path="/doctorate" element={<Doctorate />} />

        {/* 🎂 40th Birthday Invitation Page (NO AUTH REQUIRED - Public Page) */}
        <Route path="/bday" element={<BdayPage />} />
        <Route path="/bday/40years" element={<BdayPage />} />
        <Route path="/bday/40years/:slug" element={<BdayRsvpPage />} />

        {/* 🚀 BUSINESS BUILDER PAGES (NO AUTH REQUIRED - Public Pages) */}
        <Route path="/businessbuilder" element={<BusinessBuilder />} />
        <Route path="/businessbuilder/tokenomics" element={<Tokenomics />} />
        <Route path="/businessbuilder/governance" element={<Governance />} />
        <Route path="/businessbuilder/treasury" element={<Treasury />} />
        <Route path="/businessbuilder/marketplace" element={<Marketplace />} />
        <Route path="/businessbuilder/tithing-calculator" element={<TithingCalculator />} />
        <Route path="/businessbuilder/rewards" element={<Rewards />} />
        <Route path="/businessbuilder/journey" element={<Journey />} />
        {/* Leaderboard handled by rewards module config (public route) */}

        {/* 🎁 REWARDS MODULE - Routes handled by rewardsModuleConfig in modules array */}

        {/* 🎤 LIVE EVENT PAGES (NO AUTH REQUIRED - Public Webinar Funnel) */}
        <Route path="/live-event/interest" element={<LiveEventInterest />} />
        <Route path="/live-event/register" element={<LiveEventRegister />} />
        <Route path="/live-event/live" element={<LiveEventLive />} />
        <Route path="/live-event/purchase" element={<LiveEventPurchase />} />
        <Route path="/live-event/admin" element={<LiveEventAdmin />} />
        <Route path="/live-event/registrations" element={<LiveEventRegistrations />} />

        {/* 📅 PUBLIC BOOKING PAGES (NO AUTH REQUIRED) */}
        {/* These routes allow public visitors to book appointments without logging in */}
        <Route path="/book/:slug" element={<PublicBookingPage />} />
        <Route path="/book/:slug/:eventTypeId" element={<PublicBookingEventPage />} />
        <Route path="/book/:slug/confirm" element={<PublicBookingConfirmPage />} />
        <Route path="/book/:slug/success" element={<PublicBookingSuccessPage />} />
        <Route path="/calendars/booking/manage/:token" element={<ManageBooking />} />

        {/* Public Proposal Pages */}
        <Route path="/offers/onegroup" element={<OneGroupProposal />} />
        <Route path="/offers/wattdrop" element={<WattDropProposal />} />
        <Route path="/offers/paulniederer" element={<PaulNiedererProposal />} />
        {/* <Route path="/offers/onegroupstructured" element={<OneGroupStructuredProposal />} /> */}

        {/* Event routes */}
        <Route path="/events/tom/1" element={<Event1Page />} />
        <Route path="/events/tom/2" element={<Event2Page />} />
        <Route path="/events/tom/3" element={<Event3Page />} />
        <Route path="/events/tom/4" element={<Event4Page />} />
        <Route path="/events/tom/block52" element={<Block52EventPage />} />
        <Route path="/events/tom/chris" element={<ChrisEventPage />} />

        {/* Dynamic Module Routes — permissions resolved from backend for migrated modules */}
        {resolvedModules.map((module) =>
          module.routes?.map((route: any) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                (route as any).public ? (
                  <Suspense fallback={<LoadingFallback />}>
                    <route.component />
                  </Suspense>
                ) : (
                  <ProtectedRoute requiredPermissions={route.permissions || []}>
                    <Suspense fallback={<LoadingFallback />}>
                      <route.component />
                    </Suspense>
                  </ProtectedRoute>
                )
              }
            />
          ))
        )}

        {/* Website routes */}
        <Route path="/websites/new" element={<NewWebsiteForm />} />
        <Route path="/websites" element={<WebsitesList />} />
        <Route path="/website/:id" element={<WebsiteDetails />} />

        {/* Bills module now uses dynamic routing - see billsModuleConfig */}
        {/* Special bill routes not in the module */}
        <Route path="/bill/:id/payment-success" element={<BillPaymentSuccess />} />
        <Route path="/bill-chaser" element={<BillChaserPage />} />
        
        {/* Phone System routes */}
        <Route path="/phone-system" element={<PhoneSystemDashboard />} />
        <Route path="/phone-system/browser-call" element={<BrowserCall />} />
        <Route path="/phone-system/numbers" element={<PhoneNumbersList />} />
        <Route path="/phone-system/recordings" element={<CallRecordings />} />
        <Route path="/phone-system/assignments" element={<PhoneNumberAssignmentAdmin />} />

        {/* Vapi Voice AI routes */}
        <Route path="/vapi" element={<VapiDashboard />} />
        <Route path="/vapi/web-call" element={<WebCall />} />
        <Route path="/vapi/call-logs" element={<CallLogs />} />
        <Route path="/vapi/call/:callId" element={<CallDetails />} />
        <Route path="/vapi/workflows" element={<Workflows />} />
        <Route path="/vapi/todos" element={<VapiTodos />} />

        {/* Product routes */}
        <Route path="/products/new" element={<NewProductForm />} />
        <Route path="/products/:id" element={<ProductView />} />
        <Route path="/products" element={<AllProducts />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/order/:id" element={<OrderDetails />} />

        {/* Project routes - Now handled by dynamic routing via projectsModuleConfig */}

        {/* Company routes */}
        <Route path="/companies" element={<CompaniesList />} />
        <Route path="/companies/new" element={<NewCompany />} />
        <Route path="/companies/:id" element={<CompanyDetails />} />
        <Route path="/companies/edit/:id" element={<EditCompany />} />

        {/* Email routes - Now handled by dynamic routing via emailsModuleConfig */}

        {/* Calendar routes */}
        <Route path="/calendar/admin/calendars" element={<CalendarAccountsAdmin />} /> {/* MASTER ADMIN: Manage all calendars */}

        {/* Knowledge Base routes - Admin only */}
        {/* KnowledgeBase routes removed - module deleted */}

        {/* Permissions Matrix */}
        <Route path="/permissions" element={<Permissions />} />

        {/* Subscription/Billing routes */}
        <Route path="/subscriptions" element={<SubscriptionsDashboard />} />                    {/* ADMIN: Overview dashboard of all client subscriptions, revenue stats */}
        <Route path="/subscriptions/manage" element={<ManageSubscriptions />} />                {/* ADMIN: Manage existing subscriptions (cancel, modify, view details) */}
        <Route path="/subscriptions/create" element={<CreateSubscription />} />                 {/* ADMIN: Create new recurring subscriptions for clients */}
        <Route path="/subscriptions/payment-methods" element={<PaymentMethods />} />            {/* ADMIN: Add/remove client credit cards for future billing */}
        <Route path="/subscriptions/invoices" element={<InvoiceHistory />} />                   {/* ADMIN/CLIENT: View invoice history and payment receipts */}
        <Route path="/subscriptions/settings" element={<BillingSettings />} />                  {/* ADMIN: Configure billing settings, tax rates, company info */}
        <Route path="/subscriptions/manual-charging" element={<ManualCharging />} />            {/* ADMIN: One-time charges using saved payment methods */}
        <Route path="/subscriptions/:id/edit" element={<EditSubscription />} />                 {/* ADMIN: Edit existing subscription details */}

        {/* Admin routes */}
        <Route path="/admin" element={<AdminDashboard />} />

        {/* SEO Metadata routes - Now handled by dynamic routing via seoMetadataModuleConfig */}
        <Route path="/admin/tenants" element={<TenantsList />} />
        <Route path="/admin/tenants/new" element={<CreateTenant />} />
        <Route path="/admin/tenants/:id/edit" element={<EditTenant />} />
        <Route path="/admin/whatsnew/:moduleId" element={<WhatsNew />} />

        {/* Profile routes */}
        <Route path="/profile" element={<ViewProfile />} />                        {/* USER: View profile details */}
        <Route path="/profile/edit" element={<EditProfile />} />                  {/* USER: Edit profile information */}
        <Route path="/profile/settings" element={<Settings />} />                  {/* USER: Manage profile settings */}
        <Route path="/profile/shipping" element={<ShippingAddresses />} />         {/* USER: Manage shipping addresses */}
        <Route path="/profile/payment-methods" element={<ProfilePaymentMethods />} />     {/* USER: Manage payment methods */}
        <Route path="/profile/subscription-offers" element={<SubscriptionOffers />} />     {/* USER: View subscription offers */}
        <Route path="/profile/subscriptions" element={<CurrentSubscriptions />} />     {/* USER: Manage current subscriptions */}

        {/* Seller Profile routes */}
        <Route path="/seller-profiles" element={<SellerProfilesList />} />         {/* PUBLIC: View all seller profiles */}
        <Route path="/seller-profile/:id" element={<SellerProfileView />} />       {/* PUBLIC: View individual seller profile */}
        <Route path="/seller-profiles/new" element={<NewSellerProfile />} />       {/* USER: Create new seller profile */}

        {/* Provider Profile routes */}
        <Route path="/provider/:urlSlug" element={<ProviderView />} />             {/* PUBLIC: View provider profile by URL slug */}
        <Route path="/provider" element={<ProviderView />} />                      {/* USER: View your provider profile */}
        <Route path="/provider/edit" element={<EditProvider />} />                 {/* USER: Edit provider profile */}
        <Route path="/providers/discover" element={<DiscoverProviders />} />       {/* PUBLIC: Discover and search provider profiles */}

        {/* Research & Development routes */}
        {/* ResearchAndDesign routes removed - module deleted */}

        {/* Meeting Summarizer routes - Now handled by dynamic routing via meetingSummarizerModuleConfig */}

        {/* Domains module routes */}
        <Route path="/domains" element={<DomainsPage />} />  {/* USER: Domain search and management */}
        <Route path="/domains/:id" element={<DomainDetails />} />  {/* USER: Domain details and DNS management */}

        {/* Offers routes */}
        <Route path="/offers/maintenance" element={<MaintenanceOffer />} />  {/* PUBLIC: Business maintenance subscription offer */}
        <Route path="/offers/sitemaintenance" element={<SiteMaintenance />} />  {/* PUBLIC: Site maintenance subscription offer */}

        {/* Test routes */}
        <Route path="/test/sendemail" element={<SendTestEmail />} />
        <Route path="/test/upload" element={<TestUploadFile />} />
        <Route path="/test/upload-encrypted" element={<TestUploadEncryptedFile />} />
        <Route path="/test/auth" element={<TestAuth />} />
        <Route path="/test/cart" element={<TestClientCart />} />
        <Route path="/test/upload-pinata" element={<TestUploadFileToPinata />} />
        <Route path="/test/audio-transcribe-n8n" element={<TestUploadAudioToPinataAndTranscribeWithn8n />} />

      </Routes>
    </>
  );
};

export const App = () => {
  // Check and set tenantId if missing
  React.useEffect(() => {
    const storedTenantId = localStorage.getItem('tenantId');
    if (!storedTenantId) {
      // Use the tenantId from brandConfig as default
      if (brandConfig.tenantId) {
        localStorage.setItem('tenantId', brandConfig.tenantId);
      }
    }
  }, []);

  return (
    <Router>
      <ApolloProvider client={apolloClient}>
        <IpfsGatewayLoader>
        <AuthProvider>
          <ModulePermissionsProvider>
            <ChakraProvider theme={theme}>
              <AppContent />
            </ChakraProvider>
          </ModulePermissionsProvider>
        </AuthProvider>
        </IpfsGatewayLoader>
      </ApolloProvider>
    </Router>
  );
};
